import type { CartItem } from "./stores/cart";

const key = "system.shop.purchase-request.v1";

/** Default dose options before the cart stored `dose_offer_default`. The
 * backend defines exactly one default per treatment; it is the historical
 * product at the same price ("omitted selection means the historical
 * product"), so it never needs the durable dose contract. */
const LEGACY_DEFAULT_DOSE_OFFER_IDS = new Set(["nad-injection-1000mg"]);

export function isDefaultDoseItem(item: CartItem): boolean {
  if (!item.dose_offer_id) return false;
  return item.dose_offer_default ?? LEGACY_DEFAULT_DOSE_OFFER_IDS.has(item.dose_offer_id);
}

/** A non-default dose (e.g. NAD+ 2000 mg) changes what the pharmacy
 * dispenses and uses the durable purchase contract (a purchase request). */
export function isUpgradeDoseItem(item: CartItem): boolean {
  return Boolean(item.dose_offer_id) && !isDefaultDoseItem(item);
}

/** What the purchase call sends for a product line: the default dose is sent
 * as the plain product, exactly like any other treatment. */
export function purchaseDoseSelection(
  item: CartItem,
): { dose_offer_id?: string; dose_offer_version?: string } {
  return isUpgradeDoseItem(item)
    ? { dose_offer_id: item.dose_offer_id, dose_offer_version: item.dose_offer_version }
    : {};
}

const lineKeys = (items: CartItem[]) =>
  items.map((item) => [
    item.id,
    item.dose_offer_id,
    item.dose_offer_version,
    item.frequency,
  ]);

/** The lines to (re)send for a purchase request. Each line is its own
 * subscription, so lines paid before a later line failed leave the cart. A
 * retry of the rest resends the ORIGINAL basket: the server resumes that
 * request and skips paid lines instead of seeing a new purchase. Any other
 * change sends the cart as it is now. */
export function resumableLines(
  original: CartItem[],
  paidIds: string[],
  current: CartItem[],
): CartItem[] {
  const remaining = original.filter((item) => !paidIds.includes(item.id));
  return paidIds.length &&
    JSON.stringify(lineKeys(remaining)) === JSON.stringify(lineKeys(current))
    ? original
    : current;
}

type SavedRequest = {
  id: string;
  fingerprint: string;
  lines?: CartItem[];
  paid?: string[];
};

function readRequest(): SavedRequest | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) ?? "null");
    return typeof saved?.id === "string" && typeof saved?.fingerprint === "string"
      ? saved
      : null;
  } catch {
    /* unreadable local storage never grants a purchase */
    return null;
  }
}

const requestFingerprint = (
  lines: CartItem[],
  paymentMethod: string | null,
  promo?: string,
  ownerId?: string,
) =>
  JSON.stringify({ ownerId, items: lineKeys(lines), paymentMethod, promo: promo ?? "" });

/** The open request these items continue, if any: the same basket, or its
 * remaining lines after others were paid. This applies even when the
 * remaining lines no longer include the dose upgrade that opened it. */
export function openPurchaseRequest(
  items: CartItem[],
  paymentMethod: string | null,
  promo?: string,
  ownerId?: string,
): { id: string; lines: CartItem[] } | null {
  const saved = readRequest();
  if (!saved) return null;
  if (saved.fingerprint === requestFingerprint(items, paymentMethod, promo, ownerId))
    return { id: saved.id, lines: items };
  if (!saved.lines || !saved.paid?.length) return null;
  const lines = resumableLines(saved.lines, saved.paid, items);
  return lines !== items &&
    saved.fingerprint === requestFingerprint(lines, paymentMethod, promo, ownerId)
    ? { id: saved.id, lines }
    : null;
}

/** Keep the same request across a timeout/reload, including after some of its
 * lines were paid; a changed basket/card/code is a different explicit
 * purchase. Server-side ownership remains mandatory. */
export function purchaseRequest(
  items: CartItem[],
  paymentMethod: string | null,
  promo?: string,
  ownerId?: string,
): { id: string; lines: CartItem[] } {
  const open = openPurchaseRequest(items, paymentMethod, promo, ownerId);
  if (open) return open;
  const fingerprint = (lines: CartItem[]) =>
    requestFingerprint(lines, paymentMethod, promo, ownerId);
  const id = crypto.randomUUID();
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ id, fingerprint: fingerprint(items), lines: items, paid: [] }),
    );
  } catch {
    /* optional persistence */
  }
  return { id, lines: items };
}

/** Remember lines of the open request that were paid before a later line
 * failed, so a retry of the rest can resume the same request. */
export function recordPaidLines(requestId: string, paidIds: string[]): void {
  const saved = readRequest();
  if (!saved || saved.id !== requestId) return;
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ ...saved, paid: [...new Set([...(saved.paid ?? []), ...paidIds])] }),
    );
  } catch {
    /* optional persistence */
  }
}

/** Lines the server charged before a later line of the same cart failed. */
export function paidPurchaseLines(
  cause: unknown,
): { sanity_product_id: string; canonical_product_id: string }[] {
  const paid = (cause as { details?: { completed_items?: unknown } } | null)
    ?.details?.completed_items;
  return Array.isArray(paid)
    ? paid.filter(
        (line): line is { sanity_product_id: string; canonical_product_id: string } =>
          typeof line?.sanity_product_id === "string" &&
          typeof line?.canonical_product_id === "string",
      )
    : [];
}

export function retirePurchaseRequest(): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* optional persistence */
  }
}

/** Only a server-confirmed terminal failure permits a new payment attempt.
 * Generic declines, transport errors and reconciliation holds keep their key. */
export function isClosedPurchaseAttempt(cause: unknown): boolean {
  if (!cause || typeof cause !== "object") return false;
  const error = cause as { code?: string; details?: { purchase_attempt_closed?: boolean } };
  return error.code === "purchase_request_failed" ||
    (error.code === "charge_failed" && error.details?.purchase_attempt_closed === true);
}
